import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  adminGetWriteActionPolicyOptions,
  adminListConnectorsOptions,
  adminMicrosoftPermissionsOptions,
  listExtensionsOptions,
} from "@/api";
import { Text } from "@/components/Text";
import { useCapabilities } from "../../account/useCurrentUser";
import { isBuiltIn, stateOf } from "./setupStatus";

export const INTEGRATION_SECTIONS = ["catalog", "permissions", "connectors"] as const;
export type IntegrationSection = (typeof INTEGRATION_SECTIONS)[number];

/** Each section of Admin > Integrations has its own URL, so help and messages can link straight to it. */
export function integrationsPath(section: IntegrationSection): string {
  return section === "catalog" ? "/admin/integrations" : `/admin/integrations/${section}`;
}

/**
 * The sections of Admin > Integrations, each with a line saying where it stands (what needs setup, whether write
 * actions are allowed, connectors turned off), so an admin sees what needs attention without opening each one.
 */
export function IntegrationsTabs({ active }: Readonly<{ active: IntegrationSection }>) {
  const { t } = useTranslation("adminIntegrations", { keyPrefix: "tabs" });
  const status = useSectionStatus();
  return (
    <nav aria-label={t("label")} className="grid grid-cols-1 gap-2 md:grid-cols-3">
      {INTEGRATION_SECTIONS.map((section) => {
        const current = section === active;
        return (
          <Link
            key={section}
            to={integrationsPath(section)}
            aria-current={current ? "page" : undefined}
            className={`btn-focus-ring flex flex-col gap-0.5 rounded-lg border px-4 py-3 transition-colors ${
              current
                ? "border-content-primary bg-surface-secondary"
                : "border-border-default hover:border-border-strong hover:bg-surface-secondary"
            }`}
          >
            <Text as="span" variant="body-sm" weight="semibold">
              {t(`${section}.title`)}
            </Text>
            <Text as="span" variant="caption" tone={status[section].attention ? "warning" : "tertiary"}>
              {status[section].text}
            </Text>
          </Link>
        );
      })}
    </nav>
  );
}

interface Status {
  text: string;
  /** Something here needs an admin. */
  attention: boolean;
}

/** One line per section, from the same queries the sections use (so opening one shows it at once). */
function useSectionStatus(): Record<IntegrationSection, Status> {
  const { t } = useTranslation("adminIntegrations", { keyPrefix: "tabs" });
  const { sharepointEnabled } = useCapabilities();
  const extensions = useQuery({ ...listExtensionsOptions(), select: (r) => r.data });
  const writeActions = useQuery({ ...adminGetWriteActionPolicyOptions(), select: (r) => r.data });
  const microsoft = useQuery({
    ...adminMicrosoftPermissionsOptions(),
    enabled: sharepointEnabled,
    select: (r) => r.data,
  });
  const connectors = useQuery({ ...adminListConnectorsOptions(), select: (r) => r.data });

  const integrations = (extensions.data ?? []).filter((e) => !isBuiltIn(e));
  const needsSetup = integrations.filter((e) => stateOf(e) === "needs_setup").length;
  const catalog: Status = extensions.data
    ? {
        text: needsSetup
          ? t("catalog.needsSetup", { count: needsSetup })
          : t("catalog.ready", { count: integrations.filter((e) => stateOf(e) === "ready").length }),
        attention: needsSetup > 0,
      }
    : { text: "", attention: false };

  const notGranted = microsoft.data?.checked
    ? microsoft.data.features.filter((f) => f.offered && !f.granted).length
    : 0;
  const parts = writeActions.data
    ? [t(writeActions.data.enabled ? "permissions.writeOn" : "permissions.writeOff")]
    : [];
  if (notGranted) parts.push(t("permissions.notGranted", { count: notGranted }));
  const permissions: Status = { text: parts.join(" · "), attention: notGranted > 0 };

  const list = connectors.data ?? [];
  const blocked = list.filter((c) => c.disabledByAdmin || c.hostAllowed === false).length;
  const connectorStatus: Status = connectors.data
    ? {
        text: blocked
          ? t("connectors.blocked", { count: list.length, blocked })
          : t("connectors.count", { count: list.length }),
        attention: false,
      }
    : { text: "", attention: false };

  return { catalog, permissions, connectors: connectorStatus };
}
